/**
 * Taxa de câmbio da operação como o cliente teve: "1 {base} = {value} {quote}".
 *
 * O customerRate gravado não tem a mesma unidade em toda operação. Na tradicional é real por
 * unidade da moeda estrangeira (a taxa do contrato, antes de IOF e tarifa). Na stablecoin é a
 * cotação da perna do provedor (real por USDC; ~1,00 quando paga em dólar; moeda→USDC no app), que
 * não é a taxa entre o que o cliente pagou e o que recebeu (op 14062: dólar → euro gravou 1,011).
 * Por isso, na stablecoin (sem IOF), a taxa sai do que foi pago e recebido. Com real de um dos
 * lados, o preço fica em real (1 USD = R$ 5,26); entre duas estrangeiras, na moeda paga.
 *
 * Usado pela tela da operação, pelo portal do cliente e pela linha do tempo.
 */

import { STABLECOIN } from '../../common/constants/stablecoin';

const RATE_DECIMALS = 4;
// Moeda fraca de base (1 ARS = R$ 0,0033): com 4 casas o arredondamento passava de 1%
const SMALL_RATE = 0.01;
const SMALL_RATE_DECIMALS = 6;

/**
 * @param {Object} input
 * @param {string} input.originCurrency - moeda em que o cliente pagou (a do totalValue)
 * @param {string} input.destinationCurrency - moeda da operação (a do amount)
 * @param {number} input.totalValue
 * @param {number} input.amount
 * @param {number} input.customerRate
 * @param {boolean} input.isStablecoin
 * @returns {{ base: string, quote: string, value: number }|null} null quando não dá para saber
 */
export function operationRate({ originCurrency, destinationCurrency, totalValue, amount, customerRate, isStablecoin }) {
  const national = STABLECOIN.CURRENCIES.BRL.code;
  const origin = String(originCurrency || '').toUpperCase();
  const destination = String(destinationCurrency || '').toUpperCase();

  if (!origin || !destination || origin === destination) return null;

  if (!isStablecoin) {
    const rate = Number(customerRate);

    return rate > 0 ? { base: destination, quote: origin, value: rate } : null;
  }

  const paid = Number(totalValue);
  const received = Number(amount);

  if (!(paid > 0) || !(received > 0)) return null;

  if (destination === national) return { base: origin, quote: destination, value: received / paid };

  return { base: destination, quote: origin, value: paid / received };
}

/**
 * @param {{ base: string, quote: string, value: number }|null} rate
 * @param {string} [locale]
 * @returns {string|null} "1 EUR = US$ 1,1770"
 */
export function formatOperationRate(rate, locale = 'pt-BR') {
  if (!rate) return null;

  let price;
  const decimals = rate.value < SMALL_RATE ? SMALL_RATE_DECIMALS : RATE_DECIMALS;

  try {
    price = new Intl.NumberFormat(locale, { style: 'currency', currency: rate.quote, minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(rate.value);
  } catch (error) {
    // Código fora do padrão ISO (token): número e código
    price = `${new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(rate.value)} ${rate.quote}`;
  }

  return `1 ${rate.base} = ${price}`;
}
